import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { MdSearch } from "react-icons/md";
// import FinanceDash from "../../../../../components/features/finance/financel3/FinanceDash";
// import SalesFinance from "./SalesFinance";
// import SalesPiechart from "../PiechartSalesFinance/SalesPiechart";
// import SearchSalesGrossProfit from "../PiechartSalesFinance/SearchSalesGrossProfit";
// import SearchedSalesNetMargin from "../PiechartSalesFinance/SearchedSalesNetMargin";
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSnackbar } from "notistack";
// import AllServiceTable from "./AllServiceTable";
// import SliderValueLabel from "@mui/material/Slider/SliderValueLabel";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import { fetchAllFinancial } from "../../../../store/slices/editFinanceSlice";
import { fetchSalesTaxes } from "../../../../store/slices/salesTaxesSlice";
import { fetchAllProjects } from "../../../../store/slices/projectsSlice";
import { setOtheeExpensesData } from "../../../../store/slices/filteredDataSlice";
import { fetchCompanyProfiles } from "../../../../store/slices/companyProfileSlice";
import { fetchOtherExpenses } from "../../../../store/slices/otherexpenses";
import { fetchSalesHourlyRates } from "../../../../store/slices/salesHourlyRateSlice";
import { fetchLeads } from "../../../../store/slices/leadsSlice";
import { fetchEmployees } from "../../../../store/slices/employeeSlice";
import { fetchWorkEntries } from "../../../../store/slices/workentriesSlice";
import { fetchHardwareDescriptions } from "../../../../store/slices/hardwareDescriptionSlice";
import { fetchDescriptionTasks } from "../../../../store/slices/DescriptionTask";
import { fetchQuotations } from "../../../../store/slices/hourlyRates";
import { fetchGroups } from "../../../../store/slices/groupSlice";
import { updateCostPerService } from "../../../../store/slices/costPerServiceUpdateSlice";
import { useManagerActualExpense } from "../../../../utils/useManagerActualExpense";
import {
  computeManagerQuotedProjectExpense,
  computeManagerActualProjectExpense,
  collectManagerReassignmentProjectIds,
} from "./ManagerActualAllServiceTable";
import { resolveEntityId } from "../../../../utils/salespersonRatesScope";
import { shouldShowManagerQuotedExpense } from "../../../../utils/managerPlExpenseVisibility";
import {
  isFinancePlSyntheticTrainingInjectionRow,
  isTrainingExpenseDisplayRow,
  isTrainingExpenseEntry,
} from "../../../../utils/trainingOtherExpenseSync";

function isManagerPlTrainingDescriptionRow(desc) {
  if (!desc || typeof desc !== "object") return false;
  if (isFinancePlSyntheticTrainingInjectionRow(desc)) return true;
  if (isTrainingExpenseEntry(desc)) return true;
  if (isTrainingExpenseDisplayRow(desc)) return true;
  const raw = String(desc?.description ?? desc?.Description ?? "").toLowerCase();
  if (raw.includes("training")) return true;
  return false;
}

/** Actual P&L: rates from the currently assigned manager on the tracking row, not the original quoted manager. */
function enrichActualWithCurrentManagerRates(
  projectExpenseRow,
  allProjects,
  managerId,
  quotations
) {
  if (!projectExpenseRow) return projectExpenseRow;
  const mgrId = String(managerId || "").trim();

  const enrichList = (list) =>
    (list || []).map((item) => {
      const typeService = String(item.typeservice || item.TypeService || "").toLowerCase();
      if (typeService.includes("hardware")) {
        return {
          ...item,
          hourlysalaray: Number(item.hourlysalaray) || 0,
          overheadpercentage: 0,
        };
      }

      const trackingProject = (allProjects || []).find(
        (p) =>
          String(p._id) === String(item.projectid) && p.isProjecttracking === true
      );
      const personId = String(item.taskownerid || "");
      const assignedRates = trackingProject?.ManagerAssignedRates || [];
      const fromAssigned =
        assignedRates.find(
          (r) =>
            String(r.personid) === personId &&
            item.designation &&
            String(r.designation || "").trim() === String(item.designation || "").trim()
        ) || assignedRates.find((r) => String(r.personid) === personId);

      if (fromAssigned && Number(fromAssigned.hourlyrate) > 0) {
        return {
          ...item,
          hourlysalaray: Number(fromAssigned.hourlyrate),
          overheadpercentage: 0,
        };
      }

      if (mgrId) {
        let matchedQuote = (quotations || []).find(
          (q) =>
            String(q.personid || "") === personId &&
            resolveEntityId(q.createdBy) === mgrId
        );
        if (!matchedQuote && item.designation) {
          matchedQuote = (quotations || []).find(
            (q) =>
              q.designation === item.designation &&
              String(q.personid || "") === personId &&
              resolveEntityId(q.createdBy) === mgrId
          );
        }
        if (matchedQuote) {
          return {
            ...item,
            hourlysalaray: Number(matchedQuote.hourlysalaray) || 0,
            overheadpercentage: Number(matchedQuote.overheadpercentage) || 0,
          };
        }
      }

      const role = String(item.designation || "").toLowerCase();
      if (role.includes("manager") && Number(trackingProject?.managerHourlyRate) > 0) {
        return {
          ...item,
          hourlysalaray: Number(trackingProject.managerHourlyRate),
          overheadpercentage: 0,
        };
      }
      if (role.includes("operation") && Number(trackingProject?.operationHourlyRate) > 0) {
        return {
          ...item,
          hourlysalaray: Number(trackingProject.operationHourlyRate),
          overheadpercentage: 0,
        };
      }

      return {
        ...item,
        hourlysalaray: Number(item.hourlysalaray) || 0,
        overheadpercentage: Number(item.overheadpercentage) || 0,
      };
    });

  return {
    ...projectExpenseRow,
    TaskList: enrichList(projectExpenseRow.TaskList),
    Description: [...(projectExpenseRow.Description || [])],
    HardwareList: [...(projectExpenseRow.HardwareList || [])],
  };
}

/** Original manager after SP reassigned the lead to another manager (quoted stays, actual is N/A). */
function isFormerManagerAfterReassignment(
  quotationIdOrName,
  allProjects,
  loggedManagerId,
  email,
  getProjQuotationId
) {
  const loggedId = String(loggedManagerId || "").trim();
  const loggedEmail = String(email || "").trim().toLowerCase();
  if (!loggedId && !loggedEmail) return false;

  const needle = String(quotationIdOrName ?? "").trim();
  let matches = (allProjects || []).filter(
    (p) => String(getProjQuotationId(p)) === needle
  );
  if (!matches.length) {
    matches = (allProjects || []).filter(
      (p) => String(p.ProjectName || "").trim().toLowerCase() === needle.toLowerCase()
    );
  }
  if (!matches.length) return false;

  const emailsOnRow = (p) =>
    (p.AssignedManagerEmail || []).map((e) => String(e || "").trim().toLowerCase());
  const primaryEmail = (p) => emailsOnRow(p)[0] || "";
  const rowManagerId = (p) => String(p.managerid || p.managerId || "").trim();

  const loggedStillOnLead = matches.some((p) => {
    if (loggedEmail && emailsOnRow(p).includes(loggedEmail)) return true;
    return loggedId && rowManagerId(p) === loggedId;
  });
  if (!loggedStillOnLead) return false;

  const isQuotedLockRow = (p) =>
    p.isProjectQuoted === true ||
    p.isProjectQuoted === 1 ||
    String(p.isProjectQuoted || "").toLowerCase() === "true";

  const assignmentRows = matches.filter(
    (p) => p.isProjecttracking === false && !isQuotedLockRow(p)
  );
  const rowsForReassignCheck = assignmentRows.length
    ? assignmentRows
    : matches.filter((p) => p.isProjecttracking === true);

  if (!rowsForReassignCheck.length) {
    rowsForReassignCheck.push(
      ...matches.filter((p) => p.isProjecttracking === false)
    );
  }

  return rowsForReassignCheck.some((p) => {
    const primary = primaryEmail(p);
    const mgr = rowManagerId(p);
    if (loggedEmail && primary && primary !== loggedEmail) return true;
    if (loggedId && mgr && mgr !== loggedId) return true;
    return false;
  });
}

/** Prefer exact quotationId match so Actual Expenses use the correct tracking row (avoids wrong project / string-sum bugs). */
function matchQuotProjectForActualExpenses(projectList, quotationIdOrName) {
  const needle = String(quotationIdOrName ?? "").trim();
  if (!needle) return undefined;
  const arr = Array.isArray(projectList) ? projectList : [];
  const byQuotationId = arr.find((p) => {
    const qid = p?.quotationId;
    if (qid === undefined || qid === null) return false;
    return String(qid).trim() === needle;
  });
  if (byQuotationId) return byQuotationId;
  return arr.find(
    (p) =>
      String(p?.quotationId ?? p?.ProjectName ?? "").trim() === needle ||
      String(p?.ProjectName ?? "").trim() === needle
  );
}

const ManagerFinance = ({ isViewOnly = false }) => {
  const dispatch = useDispatch();
  const currency = useCurrencySymbol();
  const { enqueueSnackbar } = useSnackbar();
  const formatDecimalAmount = useMemo(() => {
    const countryCode = String(localStorage.getItem("countrycode") || "").trim();
    const region = String(localStorage.getItem("region") || "")
      .trim()
      .toUpperCase();
    const isInLocale =
      countryCode === "+91" ||
      countryCode === "91" ||
      region === "IN" ||
      region === "INDIA";
    const locale = isInLocale ? "en-IN" : "en-US";
    return (num) => {
      if (num === undefined || num === null) return "";
      const value = Number(num);
      if (isNaN(value)) return "";
      return value.toLocaleString(locale, {
        maximumFractionDigits: 10,
        minimumFractionDigits: 0,
      });
    };
  }, []);
  const quotationProjectsdata = useManagerActualExpense();
  const [showAllLeadTable, setShowAllLeadTable] = useState(true);
  const [l3Leads, setL3Leads] = useState([]);
  const [loggedUserData, setLoggedUserData] = useState([]);
  const [GroupNamesL3, setGroupNamesL3] = useState([]);
  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedProjectName, setSelectedProjectName] = useState({});
  const [AssignedGroup, setAssignedGroup] = useState("All");
  const [clientFilter, setClientFilter] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [yearData, setYearData] = useState([]);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [expandedYear, setExpandedYear] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYears, setSelectedYears] = useState([currentYear.toString()]); // Store selected years
  const [searchedLeadReports, setSearchedLeadReports] = useState([]);
  const dropdownRefclient = useRef(null);
  const [filteredQuotation, setFilteredQuotation] = useState(null);
  const { group } = useSelector((state) => state.group);
  const { leads } = useSelector((state) => state.leads);
  const { taxes } = useSelector((state) => state.salesTaxes);
  const { projects } = useSelector((state) => state.projects);
  const { entries } = useSelector((state) => state.workentries);
  const { items } = useSelector((state) => state.hardwareDescription);
  const { tasks } = useSelector((state) => state.descriptionTasks);
  const { quotations } = useSelector((state) => state.hourlyRateQuotation);
  const { employees } = useSelector((state) => state.employees);
  const { items: otherExpenses } = useSelector((state) => state.otherExpenses);
  const [perdiem, setperdiem] = useState(0);
  const [averagecost, setaveragecost] = useState(0);
  const [financeDataProject, setfinanceDataProject] = useState([]);
  const [leadData, setLeadData] = useState([]);
  const [currentEmployee, setcurrentEmployee] = useState();
  const email = localStorage.getItem("email")
  const [filteredTableData, setFilteredTableData] = useState([]);
  // Differentiate by quotationId so same ProjectName shows one row per quotation with its own values
  const getRowQuotationId = useCallback(
    (row) => row?.quotationId ?? row?.QuotationId ?? row?.ProjectName,
    []
  );
  const getProjQuotationId = useCallback(
    (proj) => proj?.quotationId ?? proj?.QuotationId ?? proj?.ProjectName,
    []
  );
  const resolveId = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "string" || typeof value === "number") return String(value).trim();
    return String(value?._id || value?.id || "").trim();
  }, []);
  const getGroupNameById = useCallback((groupId) => {
    const resolvedGroupId = resolveId(groupId);
    if (!resolvedGroupId) return "";
    const matchedGroup = (group || []).find(
      (grp) => resolveId(grp?._id || grp?.id) === resolvedGroupId
    );
    return matchedGroup?.newGroupName || matchedGroup?.name || "";
  }, [group, resolveId]);
  const getGroupIdFromRow = useCallback((row) => {
    const directId =
      resolveId(row?.groupid) ||
      resolveId(row?.lead?.groupid) ||
      resolveId(row?.projectdata?.groupid) ||
      resolveId(row?.project?.groupid);
    if (directId) return directId;

    const rowQId = getRowQuotationId(row);
    const matchedProject = financeDataProject.find((proj) => {
      const projQId = getProjQuotationId(proj);
      if (projQId && rowQId) return String(projQId) === String(rowQId);
      return (
        String(proj?.ProjectName || "").trim().toLowerCase() ===
        String(row?.ProjectName || "").trim().toLowerCase()
      );
    });
    return resolveId(matchedProject?.groupid);
  }, [financeDataProject, resolveId, getRowQuotationId, getProjQuotationId]);
  const getGroupDisplayName = useCallback((row) => {
    return (
      getGroupNameById(getGroupIdFromRow(row)) ||
      row?.AssignedGroup ||
      ""
    );
  }, [getGroupIdFromRow, getGroupNameById]);
  useEffect(() => {
    const employee = employees.find((emp) => emp.EmpEmail === email);
    setcurrentEmployee(employee)
  }, [email, employees]);

  useEffect(() => {
    setLeadData(leads)
  }, [leads]);

  useEffect(() => {
    const filteredProject = projects.filter(
      (proj) => proj.isProjecttracking === false
    );

    const qualifiedGroups = filteredProject.filter((project) => {
      // Must have at least one assigned person
      if (!project?.Assignedperson?.length) return false;

      // At least ONE assigned person must meet criteria
      return project.Assignedperson.some((person) =>
        (
          person.salespersonaction === "accept" ||
          person.salespersonaction === "project not-assigned" ||
          person.salespersonaction === "project not-assigned"

        )
        &&
        (
          person.salespersonstatus === "response received" ||
          person.salespersonstatus === "project approved" ||
          person.salespersonstatus === "project not-approved" ||
          person.salespersonstatus === "project not-assigned"
        )
        // && project.email === email
      );
    });

    const emailFilteredGroups = qualifiedGroups.filter((project) => {
      // Check if logged-in email exists in AssignedManagerEmail array
      return project?.AssignedManagerEmail?.includes(email);
    });

    setfinanceDataProject(emailFilteredGroups)
  }, [projects, email]);

  const trackingFinanceDataProject = useMemo(() => {
    return projects.filter((proj) => {
      if (proj.isProjecttracking !== true || !proj?.Assignedperson?.length) return false;
      if (!proj.AssignedManagerEmail?.includes(email)) return false;
      return proj.Assignedperson.every(
        (person) =>
          person.salespersonstatus === "project ongoing" ||
          person.salespersonstatus === "project complete"
      );
    });
  }, [projects, email]);


  useEffect(() => {
    dispatch(fetchAllFinancial());
    dispatch(fetchSalesTaxes());
    dispatch(fetchAllProjects());
    dispatch(setOtheeExpensesData())
    dispatch(fetchCompanyProfiles());
    dispatch(fetchOtherExpenses());
    dispatch(fetchSalesHourlyRates());
    dispatch(fetchQuotations());
  }, [dispatch]);
  const [matchedProjects, setMatchedProjects] = useState([]); // always an array
  const [matchedEntries, setMatchedEntries] = useState([]); // ✅ will store matched entries
  useEffect(() => {
    const selQuotationId = getRowQuotationId(selectedProjectName);
    if (selQuotationId && financeDataProject?.length) {
      const matches = financeDataProject.filter(
        (proj) => getProjQuotationId(proj) === selQuotationId
      );
      setMatchedProjects(matches);
    } else {
      setMatchedProjects([]);
    }
  }, [selectedProjectName, financeDataProject, getRowQuotationId, getProjQuotationId]);

  useEffect(() => {
    if (matchedProjects.length && entries.length) {
      const matchedIds = matchedProjects.map((proj) => proj._id);
      const filteredEntries = entries.filter((entry) =>
        matchedIds.includes(entry.projectid)
      );

      const matchedPersons = matchedProjects.flatMap((project) =>
        (project.Assignedperson || [])
          .map((person) => ({
            ...person,
            projectid: project._id,
            ProjectName: project.ProjectName, // optional
          }))
      );

      const matchedEmployees = employees.filter((employee) =>
        matchedPersons.some(
          (person) =>
            person.email?.trim().toLowerCase() ===
            employee.EmpEmail?.trim().toLowerCase()
        )
      );

      const matchedPeopleEntries = filteredEntries.filter((entry) =>
        matchedEmployees.some(
          (employee) => employee._id?.trim() === entry.taskownerid?.trim()
        )
      );

      setMatchedEntries(matchedPeopleEntries);
    } else {
      setMatchedEntries([]);
    }
  }, [matchedProjects, entries, employees]);

  const [matchedHardwareEntries, setMatchedHardwareEntries] = useState([]); // ✅ will store matched entries

  useEffect(() => {
    if (matchedProjects.length && items.length) {
      const matchedIds = matchedProjects.map((proj) => proj._id);
      const filteredEntries = items.filter((entry) =>
        matchedIds.includes(entry.projectid)
      );

      const matchedPersons = matchedProjects.flatMap((project) =>
        (project.Assignedperson || [])
          .map((person) => ({
            ...person,
            projectid: project._id,
            ProjectName: project.ProjectName, // optional
          }))
      );

      const matchedEmployees = employees.filter((employee) =>
        matchedPersons.some(
          (person) =>
            person.email?.trim().toLowerCase() ===
            employee.EmpEmail?.trim().toLowerCase()
        )
      );

      const matchedPeopleEntries = filteredEntries.filter((entry) =>
        matchedEmployees.some(
          (employee) => employee._id?.trim() === entry.taskownerid?.trim()
        )
      );

      setMatchedHardwareEntries(matchedPeopleEntries);
    } else {
      setMatchedHardwareEntries([]);
    }
  }, [matchedProjects, items, employees]);

  useEffect(() => {
    if (matchedHardwareEntries.length) {
      setMatchedEntries(prevEntries => [...prevEntries, ...matchedHardwareEntries]);
    }

  }, [matchedHardwareEntries]);

  const [groupedProj, setGroupedProj] = useState({});
  const [finalMappedEntries, setFinalMappedEntries] = useState([]);

  useEffect(() => {
    if (matchedEntries.length && employees.length) {
      const mapped = matchedEntries.map((entry) => {
        const matchedEmp = employees.find((emp) => emp._id === entry.taskownerid);

        // Check if this is a hardware entry (has hardware-specific properties)
        const isHardwareEntry = entry.hasOwnProperty('assemblepartdescription') ||
          entry.hasOwnProperty('partno') ||
          entry.hasOwnProperty('quantity');

        if (isHardwareEntry) {
          // Map hardware entries
          return {
            createdBy: {
              email: matchedEmp?.EmpEmail || "",
              name: `${matchedEmp?.EmpFirstName || ""} ${matchedEmp?.EmpLastName || ""}`,
              role: matchedEmp?.customRoles || [],
            },
            assemblepartdescription: entry.assemblepartdescription,
            partno: entry.partno,
            quantity: entry.quantity,
            unitprice: entry.unitprice,
            totalprice: entry.totalprice,
            permonth: entry.permonth,
            taskstatus: entry.taskstatus,
            _id: entry._id,
            taskownerid: entry.taskownerid,
            TypeService: entry.typeservice || "Hardware", // Use the existing typeservice field
            projectid: entry.projectid,
          }
        } else {
          // Map software/cloud/installation entries (your existing logic)
          return {
            createdBy: {
              email: matchedEmp?.EmpEmail || "",
              name: `${matchedEmp?.EmpFirstName || ""} ${matchedEmp?.EmpLastName || ""}`,
              role: matchedEmp?.customRoles || [],
            },
            task: entry.task,
            hoursassigned: entry.hoursassigned,
            taskStatus: entry.taskStatus,
            notes: entry.notes,
            _id: entry._id,
            taskownerid: entry.taskownerid,
            TypeService: entry.typeservice || matchedEmp?.servicetype,
            projectid: entry.projectid,
          };
        }
      });

      setFinalMappedEntries(mapped);
    } else {
      setFinalMappedEntries([]);
    }
  }, [matchedEntries, employees]);

  useEffect(() => {
    if (!projects || projects.length === 0 || !employees) return;

    const grouped = {};
    projects.forEach((proj) => {
      const quotationId = getProjQuotationId(proj);
      const typeService = proj.TypeService;
      const projectId = proj._id;

      const matchedEntries = finalMappedEntries.filter(
        (entry) =>
          entry.TypeService === typeService && entry.projectid === projectId
      );

      if (matchedEntries.length === 0) return;
      const salesperson = employees.find(
        (emp) => emp.EmpEmail === proj.SalesPersonEmail
      );
      const filteredProject = {
        TypeService: proj.TypeService,
        ResourceType: proj.ResourceType,
        ProjectName: proj.ProjectName,
        QuotedValue: proj.ServiceValue,
        ProjectMargin: proj.Salesmargin,
        WorkEntries: matchedEntries,
        groupid: proj.groupid,
        AssignedManagerEmail: proj.AssignedManagerEmail,
        Assignedperson: proj.Assignedperson,
        ProjectID: proj._id,
        ProjectNotes: proj?.ProjectFinanceNotes,
        SalesPersonId: salesperson ? salesperson._id : null,
        quotationId,
      };

      if (!grouped[quotationId]) grouped[quotationId] = {};
      if (!grouped[quotationId][typeService]) grouped[quotationId][typeService] = [];
      grouped[quotationId][typeService].push(filteredProject);
    });

    setGroupedProj(grouped);
  }, [projects, employees, finalMappedEntries, getProjQuotationId]);

  const [matchedQuotations, setMatchedQuotations] = useState({});


  useEffect(() => {
    if (!quotations || !groupedProj) return;
    const updatedGroupedProj = {};
    Object.entries(groupedProj).forEach(([quotationIdKey, serviceTypes]) => {
      updatedGroupedProj[quotationIdKey] = {};
      Object.entries(serviceTypes).forEach(([typeService, serviceArray]) => {
        const updatedServiceArray = serviceArray.map((entry) => {
          const matchedQuotations = quotations.filter((q) =>
            q.designation?.toLowerCase().includes(typeService.toLowerCase()) &&
            q.createdBy === currentEmployee?._id
          );
          return { ...entry, quotationDetails: matchedQuotations };
        });
        updatedGroupedProj[quotationIdKey][typeService] = updatedServiceArray;
      });
    });
    setMatchedQuotations(updatedGroupedProj);
  }, [quotations, groupedProj, currentEmployee]);

  useEffect(() => {
    const selQuotationId = getRowQuotationId(selectedProjectName);
    if (!selQuotationId || !matchedQuotations) return;
    const match = matchedQuotations[selQuotationId];
    if (match) {
      setFilteredQuotation({ [selQuotationId]: match });
    } else {
      setFilteredQuotation(null);
    }
  }, [selectedProjectName, matchedQuotations, getRowQuotationId]);

  useEffect(() => {
    setL3Leads(leads);
    if (group) {
      setGroupNamesL3(group);
    }
  }, [leads, group]);

  useEffect(() => {
    if (l3Leads) {
      setLoggedUserData([...l3Leads].reverse());
    }
  }, [l3Leads, taxes]);

  const dropdownRefGroup = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefGroup.current &&
        !dropdownRefGroup.current.contains(event.target)
      ) {
        setIsGroupOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const dropdownRefYear = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefYear.current &&
        !dropdownRefYear.current.contains(event.target)
      ) {
        setIsYearOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefclient.current &&
        !dropdownRefclient.current.contains(event.target)
      ) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (loggedUserData.length === 0) return;

    let filtered = loggedUserData;
    // Filter by AssignedGroup (skip if "All")
    if (AssignedGroup && AssignedGroup !== "All") {
      filtered = filtered.filter(
        (lead) => getGroupIdFromRow(lead) === String(AssignedGroup)
      );
    }

    // Filter by ClientName (skip if "All")
    if (clientFilter && clientFilter !== "All") {
      filtered = filtered.filter((lead) => lead.ClientName === clientFilter);
    }

    // One row per unique quotationId: same project name can have multiple rows with different values
    const expanded = [];
    filtered.forEach((lead) => {
      const leadQId = getRowQuotationId(lead);
      const matches = financeDataProject?.filter(
        (p) =>
          p.ProjectName === lead.ProjectName &&
          (!getGroupIdFromRow(lead) || resolveId(p.groupid) === getGroupIdFromRow(lead))
      ) || [];
      if (leadQId && leadQId !== lead.ProjectName) {
        const one = matches.find((p) => getProjQuotationId(p) === leadQId);
        expanded.push({ ...lead, quotationId: one ? getProjQuotationId(one) : leadQId });
      } else if (matches.length) {
        matches.forEach((p) => expanded.push({ ...lead, quotationId: getProjQuotationId(p) }));
      } else {
        expanded.push({ ...lead, quotationId: lead.ProjectName });
      }
    });
    setSearchedLeadReports(expanded);
  }, [loggedUserData, AssignedGroup, clientFilter, financeDataProject, getGroupIdFromRow, resolveId, getRowQuotationId, getProjQuotationId]);


  // Inside your component
  const uniqueClients = [
    ...new Map(
      leadData
        .filter((lead) =>
          AssignedGroup === "All"
            ? true                          // ✅ show all clients
            : getGroupIdFromRow(lead) === String(AssignedGroup)
        )
        .map((lead) => [
          lead.ClientName?.trim().toLowerCase(), // key for uniqueness
          lead.ClientName?.trim(),               // display value
        ])
    ).values(),
  ].sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" }));
  // sort alphabetically, ignore case





  const itemsPerPage = 10;
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endIdx = startIdx + itemsPerPage;
  const currentItems = filteredTableData?.slice(startIdx, endIdx); // Changed from searchedLeadReports to filteredTableData
  const totalItems = filteredTableData?.length; // Changed from searchedLeadReports.length to filteredTableData?.length
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const sortAlphabetically = (targetToSort, order) => {
    const key = `${targetToSort}-${order}`;
    const sortedReports = [...filteredTableData];

    switch (key) {
      case "projectname-az":
        sortedReports.sort((a, b) =>
          a.ProjectName?.trim()
            .toLowerCase()
            .localeCompare(b.ProjectName?.trim().toLowerCase())
        );
        break;

      case "projectname-za":
        sortedReports.sort((a, b) =>
          b.ProjectName?.trim()
            .toLowerCase()
            .localeCompare(a.ProjectName?.trim().toLowerCase())
        );
        break;

      case "groupname-az":
        sortedReports.sort((a, b) =>
          getGroupDisplayName(a)?.trim()
            .toLowerCase()
            .localeCompare(getGroupDisplayName(b)?.trim().toLowerCase())
        );
        break;

      case "groupname-za":
        sortedReports.sort((a, b) =>
          getGroupDisplayName(b)?.trim()
            .toLowerCase()
            .localeCompare(getGroupDisplayName(a)?.trim().toLowerCase())
        );
        break;

      case "clientname-az":
        sortedReports.sort((a, b) =>
          a.ClientName?.trim()
            .toLowerCase()
            .localeCompare(b.ClientName?.trim().toLowerCase())
        );
        break;

      case "clientname-za":
        sortedReports.sort((a, b) =>
          b.ClientName?.trim()
            .toLowerCase()
            .localeCompare(a.ClientName?.trim().toLowerCase())
        );
        break;

      default:
        console.error("Invalid Order");
    }

    setFilteredTableData(sortedReports);
  };


  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchInputChange = (e) => {
    const query = e.target.value; // Remove leading/trailing spaces
    // const lowerQuery = query.replace(/,/g, '').toLowerCase();
    // const lowerCaseQuery = lowerQuery.toLowerCase().trim();

    setSearchQuery(query);
    filterLeadReports(query);
    setCurrentPage(1); // ✅ Reset to first page when searching
  };

  // const filterLeadReports = (query) => {
  //   // const lowerCaseQuery = query.toLowerCase().trim();
  //   const lowerCaseQuery = query
  //     .toLowerCase()
  //     .trim()
  //     .replace(/,/g, ""); // ✅ remove commas for search only

  //   const filtered = loggedUserData?.filter((leadReport) => {
  //     const projectName = leadReport?.ProjectName?.toLowerCase() || "";
  //     const quotedValue = String(leadReport?.QuotedValue || "")?.toLowerCase();
  //     const ClientName = String(leadReport?.ClientName || "")?.toLowerCase();
  //     const AssignedGroup = String(leadReport?.AssignedGroup || "")?.toLowerCase();

  //     // Search by explicit quoted expenses
  //     const quotedExpenses = String(leadReport?.quotedExpense || "")?.toLowerCase();

  //     // Search by calculated expenses (fallback when no quoted expense is set)
  //     const calculatedExpenses = showExpensesop(leadReport?.ProjectName)?.toLowerCase().replace(/[$,]/g, "");

  //     return (
  //       projectName.includes(lowerCaseQuery) ||
  //       quotedValue.includes(lowerCaseQuery) ||
  //       quotedExpenses.includes(lowerCaseQuery) ||
  //       calculatedExpenses.includes(lowerCaseQuery) ||
  //       ClientName.includes(lowerCaseQuery) ||
  //       AssignedGroup.includes(lowerCaseQuery)
  //     );
  //   });
  //   setSearchedLeadReports(filtered);
  // };

  const filterLeadReports = (query) => {
    const lowerCaseQuery = query
      .toLowerCase()
      .trim()
      .replace(/,/g, ""); // ✅ remove commas for search only

    const filtered = loggedUserData?.filter((leadReport) => {
      const projectName = leadReport?.ProjectName?.toLowerCase() || "";
      const quotedValue = String(leadReport?.QuotedValue || "")?.toLowerCase();
      const ClientName = String(leadReport?.ClientName || "")?.toLowerCase();
      const assignedGroupName = String(getGroupDisplayName(leadReport) || "")?.toLowerCase();

      // Search by explicit quoted expenses
      // const quotedExpenses = String(leadReport?.quotedExpense || "")?.toLowerCase();
      const rowId = getRowQuotationId(leadReport);
      const quotedExpenses = String(Number(showExpensesopActual(rowId)) || 0)
        .toLowerCase()
        .replace(/[$,]/g, "");
      const calculatedExpenses = String(Number(showExpensesop(rowId)) || 0)
        .toLowerCase()
        .replace(/[$,]/g, "");

      return (
        projectName.includes(lowerCaseQuery) ||
        quotedValue.includes(lowerCaseQuery) ||
        quotedExpenses.includes(lowerCaseQuery) ||
        calculatedExpenses.includes(lowerCaseQuery) ||
        ClientName.includes(lowerCaseQuery) ||
        assignedGroupName.includes(lowerCaseQuery)
      );
    });
    // One row per quotationId
    const expanded = [];
    filtered.forEach((lead) => {
      const leadQId = getRowQuotationId(lead);
      const matches = financeDataProject?.filter(
        (p) =>
          p.ProjectName === lead.ProjectName &&
          (!getGroupIdFromRow(lead) || resolveId(p.groupid) === getGroupIdFromRow(lead))
      ) || [];
      if (leadQId && leadQId !== lead.ProjectName) {
        const one = matches.find((p) => getProjQuotationId(p) === leadQId);
        expanded.push({ ...lead, quotationId: one ? getProjQuotationId(one) : leadQId });
      } else if (matches.length) {
        matches.forEach((p) => expanded.push({ ...lead, quotationId: getProjQuotationId(p) }));
      } else {
        expanded.push({ ...lead, quotationId: lead.ProjectName });
      }
    });
    setSearchedLeadReports(expanded);
  };
  useEffect(() => {
    dispatch(fetchLeads());
    dispatch(fetchGroups());
  }, [dispatch]);

  const handleClientChange = (clientValue) => {
    setClientFilter(clientValue);
    setShowClientDropdown(false);
  };

  useEffect(() => {
    let filteredData = [...loggedUserData];

    // Apply group filter
    if (AssignedGroup && AssignedGroup !== "All") {
      filteredData = filteredData.filter(
        (lead) => getGroupIdFromRow(lead) === String(AssignedGroup)
      );
    }

    // Apply client filter
    if (clientFilter && clientFilter !== "All") {
      filteredData = filteredData.filter((lead) => lead.ClientName === clientFilter);
    }

    // Define quarter ranges
    const quarterMapping = {
      "Jan - Mar": ["Jan", "Feb", "Mar"],
      "Apr - Jun": ["Apr", "May", "Jun"],
      "Jul - Sep": ["Jul", "Aug", "Sep"],
      "Oct - Dec": ["Oct", "Nov", "Dec"],
      "Apr - Jul": ["Apr", "May", "Jun", "Jul"],
      "Aug - Nov": ["Aug", "Sep", "Oct", "Nov"],
      "Dec - Mar": ["Dec", "Jan", "Feb", "Mar"],
    };

    // Filter by selected years (only if years are selected)
    if (selectedYears.length > 0) {
      filteredData = filteredData.filter((lead) => {
        const leadDate = new Date(lead.LeadDate);
        const leadYear = leadDate.getFullYear().toString();
        const leadMonth = leadDate.toLocaleString("default", {
          month: "short",
        });

        return selectedYears.some((entry) => {
          const entryStr = String(entry).trim();
          const [entryYear, ...rest] = entryStr.split(" ");
          const joinedRest = rest
            .join(" ")
            .replace(/\s*-\s*/g, " - ")
            .trim();

          const normalizedMonth = leadMonth.trim();
          const normalizedJoined = joinedRest;

          // ✅ Match year only (e.g. "2025")
          if (entryStr === leadYear) return true;

          // ✅ Match month (e.g. "2025 Feb")
          if (
            entryYear === leadYear &&
            normalizedJoined.toLowerCase() === normalizedMonth?.toLowerCase()
          )
            return true;

          // ✅ Match quarter (e.g. "2025 Apr - Jun")
          if (
            entryYear === leadYear &&
            quarterMapping[normalizedJoined]?.includes(normalizedMonth)
          )
            return true;

          return false;
        });
      });
    }

    // One row per unique quotationId
    const expanded = [];
    filteredData.forEach((lead) => {
      const leadQId = getRowQuotationId(lead);
      const matches = financeDataProject?.filter(
        (p) =>
          p.ProjectName === lead.ProjectName &&
          (!getGroupIdFromRow(lead) || resolveId(p.groupid) === getGroupIdFromRow(lead))
      ) || [];
      if (leadQId && leadQId !== lead.ProjectName) {
        const one = matches.find((p) => getProjQuotationId(p) === leadQId);
        expanded.push({ ...lead, quotationId: one ? getProjQuotationId(one) : leadQId });
      } else if (matches.length) {
        matches.forEach((p) => expanded.push({ ...lead, quotationId: getProjQuotationId(p) }));
      } else {
        expanded.push({ ...lead, quotationId: lead.ProjectName });
      }
    });
    setSearchedLeadReports(expanded);
  }, [loggedUserData, selectedYears, AssignedGroup, clientFilter, financeDataProject, getGroupIdFromRow, resolveId, getRowQuotationId, getProjQuotationId]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const allMonths = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const quarters = ["Jan - Mar", "Apr - Jun", "Jul - Sep", "Oct - Dec"];

    const data = Array.from({ length: 5 }, (_, i) => {
      const year = currentYear - i;
      return {
        Year: year,
        months: allMonths,
        quarter: quarters,
      };
    });

    setYearData(data);
  }, []);

  const handleYearSelection = (value) => {
    setSelectedYears((prev) => {
      if (prev.includes(value)) {
        // Remove value if already selected
        return prev.filter((item) => item !== value);
      } else {
        // Add value if not already selected
        return [value];
      }
    });
  };



  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchWorkEntries());
    dispatch(fetchAllProjects());
    dispatch(fetchHardwareDescriptions());
    dispatch(fetchDescriptionTasks());
    dispatch(fetchQuotations());
    dispatch(fetchOtherExpenses());
    dispatch(fetchSalesTaxes());
  }, [dispatch]);

  const [searchedCalcProjects, setSearchedCalcProjects] = useState([]);

  useEffect(() => {
    if (!searchedLeadReports || !financeDataProject) return; // guard

    // Match by quotationId so each row gets its own project data (same ProjectName, different values)
    const matchedProjects = financeDataProject.filter((row) =>
      searchedLeadReports.some((proj) => getRowQuotationId(proj) === getProjQuotationId(row))
    );
    setSearchedCalcProjects(matchedProjects);
  }, [searchedLeadReports, financeDataProject, getRowQuotationId, getProjQuotationId]);

  const [mergedProjects, setMergedProjects] = useState([]);

  useEffect(() => {
    if (!searchedCalcProjects || !tasks || !entries) return;

    const newData = searchedCalcProjects.map((proj) => {
      const matchedEntries = entries.filter(
        (entry) => entry.projectid === proj._id
      );

      const matchedDescriptions = tasks.filter(
        (task) => task.projectid === proj._id
      );
      const matchedHardware = items.filter(
        (task) => task.projectid === proj._id
      );

      return {
        ProjectName: proj.ProjectName,
        quotationId: getProjQuotationId(proj),
        TaskList: matchedEntries,
        Description: matchedDescriptions,
        TypeService: proj.TypeService,
        HardwareList: matchedHardware,
        Assignedperson: proj.Assignedperson,
        AssignedManagerEmail: proj.AssignedManagerEmail[0],
        SalesPersonEmail: proj.SalesPersonEmail,
      };
    });

    setMergedProjects(newData);
  }, [searchedCalcProjects, tasks, entries, items, getProjQuotationId]);

  const [combinedProjects, setCombinedProjects] = useState([]);

  useEffect(() => {
    if (!mergedProjects || mergedProjects.length === 0) return;

    const qId = (p) => p.quotationId ?? getProjQuotationId(p);
    const grouped = Object.values(
      mergedProjects.reduce((acc, project) => {
        const id = qId(project);
        if (!acc[id]) {
          acc[id] = {
            ProjectName: project.ProjectName,
            quotationId: id,
            salespersonEmail: project.SalesPersonEmail,
            TaskList: [],
            Description: [],
            HardwareList: [],
          };
        }
        acc[id].TaskList.push(...(project.TaskList || []));
        acc[id].Description.push(...(project.Description || []));
        acc[id].HardwareList.push(...(project.HardwareList || []));
        return acc;
      }, {})
    );

    setCombinedProjects(grouped);
  }, [mergedProjects, getProjQuotationId]);

  const [groupedProjects, setGroupedProjects] = useState([]);



  useEffect(() => {
    if (!combinedProjects?.length || !employees?.length) return;

    const updatedProjects = combinedProjects.map((project) => {
      const enrichWithDesignation = (arr = []) =>
        arr.map((item) => {
          // Prefer taskownerid first
          const emp =
            employees.find((e) => e._id === item.taskownerid) ||
            employees.find((e) => e._id === item.createdBy);

          if (emp) {
            // Only include designation if servicetype or customRoles exist
            const designationParts = [];
            if (emp.servicetype) designationParts.push(emp.servicetype);
            if (emp.customRoles?.length) designationParts.push(emp.customRoles[0]);

            return {
              ...item,
              designation: designationParts.join(" ").trim() || null,
            };
          }

          return { ...item, designation: null };
        });

      return {
        ...project,
        Description: enrichWithDesignation(project.Description),
        TaskList: enrichWithDesignation(project.TaskList),
        HardwareList: enrichWithDesignation(project.HardwareList),
      };
    });

    setGroupedProjects(updatedProjects);
  }, [combinedProjects, employees]);

  const [quotationProjects, setquotationProjects] = useState([]);
  console.log("gsdgsdg", quotationProjects)
  useEffect(() => {
    if (quotationProjects?.length) {
      dispatch(setOtheeExpensesData(quotationProjects))
    }
  }, [dispatch, quotationProjects]);



  useEffect(() => {
    if (!groupedProjects || !quotations || !employees) return;

    const managerRatesOwnerId = resolveEntityId(currentEmployee?._id);
    if (!managerRatesOwnerId) return;

    const updatedProjects = groupedProjects.map((project) => {
      // helper to map arrays and add hourlysalary with filtered quotations
      const enrichWithQuotation = (arr) =>
        arr.map((item) => {
          const personId = String(item.taskownerid || "");

          // Prefer person match (same as itemized P&L / hourly rates settings)
          let matchedQuote = (quotations || []).find(
            (q) =>
              String(q.personid || "") === personId &&
              resolveEntityId(q.createdBy) === managerRatesOwnerId
          );

          if (!matchedQuote && item.designation) {
            matchedQuote = (quotations || []).find(
              (q) =>
                q.designation === item.designation &&
                String(q.personid || "") === personId &&
                resolveEntityId(q.createdBy) === managerRatesOwnerId
            );
          }

          const hourlysalaray =
            Number(item.hourlysalaray ?? matchedQuote?.hourlysalaray ?? 0) || 0;
          const overheadpercentage =
            Number(
              item.overheadpercentage ?? matchedQuote?.overheadpercentage ?? 0
            ) || 0;

          return {
            ...item,
            hourlysalaray,
            overheadpercentage,
          };
        });

      return {
        ...project,
        TaskList: project.TaskList ? enrichWithQuotation(project.TaskList) : [],
        Description: project.Description
          ? enrichWithQuotation(project.Description)
          : [],
        HardwareList: project.HardwareList
          ? enrichWithQuotation(project.HardwareList)
          : [],
      };
    });

    setquotationProjects(updatedProjects);
  }, [groupedProjects, quotations, employees, currentEmployee]);

  const [, setInstallationDescriptions] = useState([]);

  useEffect(() => {
    if (!quotationProjects || quotationProjects.length === 0) return;

    const filteredProjects = quotationProjects
      .map((proj) => {
        const matchedDescriptions = (proj.Description || []).filter(
          (desc) =>
            desc.typeservice &&
            desc.typeservice.toLowerCase().includes("installation") &&
            desc.designation &&
            desc.designation.toLowerCase().includes("operations")
        );

        if (matchedDescriptions.length > 0) {
          return {
            ProjectName: proj.ProjectName,
            Description: matchedDescriptions,
          };
        }
        return null;
      })
      .filter(Boolean); // remove nulls

    setInstallationDescriptions(filteredProjects);
  }, [quotationProjects]);



  useEffect(() => {
    if (otherExpenses && otherExpenses.length > 0) {
      const perdiemExpense = otherExpenses.find(expense =>
        expense.description && expense.description.toLowerCase() === 'perdiem'
      );

      const averageCostExpense = otherExpenses.find(expense =>
        expense.description && expense.description.toLowerCase() === 'average cost'
      );

      setperdiem(perdiemExpense ? perdiemExpense.cost || 0 : 0);
      setaveragecost(averageCostExpense ? averageCostExpense.cost || 0 : 0);
    } else {
      setperdiem(0);
      setaveragecost(0);
    }
  }, [otherExpenses]);

  const showExpensesop = useCallback((quotationIdOrName) => {
    const matchedProjectfinal = quotationProjects.find(
      (proj) =>
        (proj.quotationId ?? proj.ProjectName) === quotationIdOrName ||
        proj.ProjectName === quotationIdOrName
    );
    if (!matchedProjectfinal) {
      return 0;
    }

    // Quoted expenses = Σ (hourly + overhead) × hours assigned + Other Expenses Amount + salesperson trip perdiem/avgcost
    return computeManagerQuotedProjectExpense(matchedProjectfinal, {
      preferActualHours: false,
      projects,
      financeDataProject,
      otherExpenses,
      includeInstallationTripCosts: true,
    });
  }, [quotationProjects, projects, financeDataProject, otherExpenses]);


  const showExpensesopActual = useCallback((quotationIdOrName) => {
    const loggedManagerId = String(currentEmployee?._id || "").trim();
    const needle = String(quotationIdOrName ?? "").trim();

    const rowTrackingProjects = (trackingFinanceDataProject || []).filter(
      (p) => {
        const matchesRow =
          String(getProjQuotationId(p)) === needle ||
          String(p.ProjectName || "").trim() === needle;
        if (!matchesRow) return false;
        if (!loggedManagerId) return true;
        return (
          String(p.managerid || p.managerId || "").trim() === loggedManagerId
        );
      }
    );

    if (!rowTrackingProjects.length) {
      return 0;
    }

    const matchedProjectfinal = matchQuotProjectForActualExpenses(
      quotationProjectsdata,
      quotationIdOrName
    );
    if (!matchedProjectfinal) {
      return 0;
    }

    const rowProjectIds = collectManagerReassignmentProjectIds(
      rowTrackingProjects,
      projects
    );

    const isHardwareManagerRow = rowTrackingProjects.some((p) =>
      String(p.TypeService || "").toLowerCase().includes("hardware")
    );
    const isInstallationManagerRow = rowTrackingProjects.some((p) =>
      String(p.TypeService || "").toLowerCase().includes("installation")
    );

    const scopedProject = {
      ...matchedProjectfinal,
      TaskList: (matchedProjectfinal.TaskList || []).filter(
        (t) =>
          rowProjectIds.has(String(t.projectid)) &&
          !isManagerPlTrainingDescriptionRow(t)
      ),
      Description: (matchedProjectfinal.Description || []).filter(
        (d) =>
          rowProjectIds.has(String(d.projectid)) &&
          !isManagerPlTrainingDescriptionRow(d)
      ),
      // Installation actual = effort + other expenses only; hardware actual includes partlist total price.
      HardwareList: isHardwareManagerRow
        ? (matchedProjectfinal.HardwareList || []).filter((hw) =>
            rowProjectIds.has(String(hw.projectid))
          )
        : [],
    };

    const currentManagerId =
      rowTrackingProjects[0]?.managerid ||
      rowTrackingProjects[0]?.managerId ||
      currentEmployee?._id;

    const enrichedProject = enrichActualWithCurrentManagerRates(
      scopedProject,
      projects,
      currentManagerId,
      quotations
    );

    // Actual = hours spent × rate + other expenses + installation trip perdiem/avgcost (salesperson)
    return computeManagerActualProjectExpense(
      enrichedProject,
      rowTrackingProjects,
      projects,
      quotations,
      {
        otherExpenses,
        employees,
        financeDataProjectList: trackingFinanceDataProject,
        quotationSalespersonEmail:
          matchedProjectfinal?.salespersonEmail ||
          matchedProjectfinal?.SalesPersonEmail ||
          rowTrackingProjects[0]?.SalesPersonEmail ||
          null,
        includeInstallationTripCosts: isInstallationManagerRow,
      }
    );
  }, [
    quotationProjectsdata,
    projects,
    trackingFinanceDataProject,
    quotations,
    currentEmployee,
    getProjQuotationId,
    otherExpenses,
    employees,
  ]);

  const shouldShowQuotedExpenseForRow = useCallback(
    (quotationIdOrName) =>
      shouldShowManagerQuotedExpense({
        quotationIdOrName,
        projects,
        loggedManagerId: currentEmployee?._id,
        email,
        getProjQuotationId,
      }),
    [projects, currentEmployee, email, getProjQuotationId]
  );

  const wasReassignedFromLoggedManager = useCallback(
    (quotationIdOrName) =>
      isFormerManagerAfterReassignment(
        quotationIdOrName,
        projects,
        currentEmployee?._id,
        email,
        getProjQuotationId
      ),
    [projects, currentEmployee, email, getProjQuotationId]
  );

  const showColor = useCallback((quotationIdOrName) => {
    const filteredProject = projects.filter(
      (proj) => proj.isProjecttracking === true
    );

    const emailFilteredGroups = filteredProject.filter((project) => {
      return project?.AssignedManagerEmail?.includes(email);
    });

    // Find by quotationId so same ProjectName with different quotationId get correct color
    const project = emailFilteredGroups.find(
      (proj) =>
        getProjQuotationId(proj) === quotationIdOrName ||
        proj.ProjectName === quotationIdOrName
    );

    if (!project || !project.Assignedperson || project.Assignedperson.length === 0) {
      return ''; // Return default color or empty string
    }

    // Get all salespersonstatus values from assigned persons
    const allStatuses = project.Assignedperson.map(person =>
      person.salespersonstatus
    );

    // Check if ALL assigned persons have "project ongoing"
    const allOngoing = allStatuses.every(status => status === "project ongoing");

    // Check if ALL assigned persons have "project complete"
    const allComplete = allStatuses.every(status => status === "project complete");

    if (allOngoing) {
      return 'text-orange-600';
    } else if (allComplete) {
      return 'text-green-600';
    } else {
      return ''; // Return default if mixed statuses or other statuses
    }

  }, [projects, email, getProjQuotationId]);

  useEffect(() => {
    if (searchedLeadReports && searchedLeadReports.length > 0) {
      const filtered = searchedLeadReports.filter((row) => {
        const rowId = getRowQuotationId(row);
        const quoted = Number(showExpensesop(rowId)) || 0;
        const actual = Number(showExpensesopActual(rowId)) || 0;
        return quoted !== 0 || actual !== 0;
      });
      setFilteredTableData(filtered);
      setCurrentPage(1);
    } else {
      setFilteredTableData([]);
    }
  }, [
    searchedLeadReports,
    financeDataProject,
    showExpensesop,
    showExpensesopActual,
    getRowQuotationId,
  ]);



  const QuotationRef = useRef(null);
  const getDescriptionValues = useCallback(
    (projectID, personID, typeService) => {
      if (!tasks) return [];
      return tasks.filter(
        (desc) => desc.projectid === projectID && desc.taskownerid === personID
      );
    },
    [tasks]
  );


  const getTotalDescriptionValue = useCallback(
    (projectID, personID, typeService) => {
      const descriptions = getDescriptionValues(projectID, personID, typeService);

      let totalValue = descriptions.reduce((sum, desc) => sum + (desc.value || 0), 0);

      // Check if this is an Installation service
      if (typeService && typeService.toLowerCase().includes('installation')) {
        // Find the maximum trip duration for this person (to avoid duplicate counting)
        let maxTripDuration = 0;
        const uniqueTripDurations = new Set();

        descriptions.forEach(desc => {
          if (desc.tripduration) {
            uniqueTripDurations.add(desc.tripduration);
            maxTripDuration = Math.max(maxTripDuration, desc.tripduration);
          }
        });

        // Use the maximum trip duration for calculation
        // If you want to use average trip duration instead, you can calculate:
        // const avgTripDuration = Array.from(uniqueTripDurations).reduce((sum, duration) => sum + duration, 0) / uniqueTripDurations.size;

        if (maxTripDuration > 0) {
          // Add: (tripduration * perdiem) + (tripduration * averageCost)
          totalValue += (maxTripDuration * perdiem) + (maxTripDuration * averagecost);
        }
      }

      return totalValue;
    },
    [getDescriptionValues, perdiem, averagecost] // Add perdiem and averageCost to dependencies
  );

  const getHardwareValues = useCallback(
    (projectID, typeService) => {
      if (!items) return [];
      return items.filter(
        (desc) =>
          desc.projectid === projectID &&
          // desc.taskownerid === personID &&
          desc.typeservice === typeService
      );
    },
    [items]
  );

  const getTotalHardwareCostValue = useCallback(
    (projectID, personID, typeService) => {
      let hardwareValue = 0;
      const hardwareCost = getHardwareValues(projectID, typeService);
      const hardwareByDescription = {};

      // Group hardware by assemblepartdescription and keep only the one with highest totalprice
      hardwareCost.forEach((hw) => {
        const description = hw.assemblepartdescription;

        if (!hardwareByDescription[description]) {
          hardwareByDescription[description] = hw;
        } else {
          // If we already have this description, keep the one with higher totalprice
          if (hw.totalprice > hardwareByDescription[description].totalprice) {
            hardwareByDescription[description] = hw;
          }
        }
      });

      // Calculate total hardware value using only the highest totalprice items
      Object.values(hardwareByDescription).forEach((hw) => {
        if (hw.totalprice) {
          hardwareValue += hw.totalprice;
        }
      });
      return hardwareValue
      // return hardwareCost.reduce((sum, desc) => sum + (desc.totalprice || 0), 0);
    },
    [getHardwareValues]
  );

  const transformFilteredQuotation = useCallback((filteredQuotation, employees) => {
    const result = [];

    Object.entries(filteredQuotation || {}).forEach(([quotationIdKey, typeServices]) => {
      Object.entries(typeServices).forEach(([typeService, items]) => {
        items.forEach((item) => {
          if (!item?.WorkEntries?.length) return;

          // Group work entries by person
          const groupedByPerson = {};
          const personRoleMap = {};

          item.WorkEntries.forEach((entry) => {
            const personName = entry?.createdBy?.name;
            const personRoles = entry?.createdBy?.role || [];
            const personId = entry?.taskownerid;

            if (!groupedByPerson[personName]) {
              groupedByPerson[personName] = { totalHours: 0, personId };
            }
            groupedByPerson[personName].totalHours += entry.hoursassigned || 0;

            if (!personRoleMap[personName]) {
              personRoleMap[personName] = { roles: personRoles, personId };
            }
          });

          // Convert grouped persons into array with costs
          Object.entries(groupedByPerson).forEach(([name, personData]) => {
            const totalHours = personData.totalHours;
            const personId = personData.personId;
            // const roles = personRoleMap[name]?.roles || [];
            const ProjectID = item?.ProjectID;
            const ProjectNotes = item?.ProjectNotes;
            const ServiceQuotedValue = item?.QuotedValue;
            const servicewiseMargin = item?.ProjectMargin;

            // CRITICAL FIX: Match quotation by personid and taskownerid
            const matchedQuotation = item?.quotationDetails?.find((q) =>
              q.personid === personId
            );

            const currentEmployee = employees.find((emp) => {
              const fullName = `${emp.EmpFirstName} ${emp.EmpLastName}`.trim().toLowerCase();
              return fullName === name.trim().toLowerCase();
            });

            const capitalizedRoles =
              currentEmployee?.customRoles?.map(
                (role) =>
                  role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
              ) || [];

            const hourlyRate =
              (matchedQuotation?.hourlysalaray || 0) +
              (matchedQuotation?.overheadpercentage || 0);

            const totalCost = totalHours * hourlyRate;

            // External helpers
            const descriptionTotal = getTotalDescriptionValue(
              item.ProjectID,
              personId,
              item.TypeService
            );

            const hardwareTotal = getTotalHardwareCostValue(
              item.ProjectID,
              personId,
              item.TypeService
            );

            const hasProjectManagerRole = capitalizedRoles.includes("Project manager");
            result.push({
              servicewiseMargin,
              ServiceQuotedValue,
              projectName: item.ProjectName || quotationIdKey,
              quotationId: item.quotationId ?? quotationIdKey,
              typeService: item.TypeService,
              resourceType: item.ResourceType,
              personId,
              ProjectID,
              ProjectNotes,
              personName: name,
              roles: capitalizedRoles,
              totalHours,
              hourlyRate,
              totalCost,
              descriptionTotal,
              hardwareTotal,
              // grandTotal:
              //   item?.TypeService?.toLowerCase().includes("hardware")
              //     ? hardwareTotal + descriptionTotal
              //     : descriptionTotal + totalCost,
              grandTotal:
                item?.TypeService?.toLowerCase().includes("hardware")
                  ? (hasProjectManagerRole ? hardwareTotal : 0) + descriptionTotal
                  : descriptionTotal + totalCost,
            });
          });
        });
      });
    });

    return result;
  }, [getTotalDescriptionValue, getTotalHardwareCostValue]);

  const groupByTypeService = (processedData) => {
    return processedData.reduce((acc, curr) => {
      if (!acc[curr.typeService]) {
        acc[curr.typeService] = [];
      }
      acc[curr.typeService].push(curr);
      return acc;
    }, {});
  };


  const processedData = useMemo(() => {
    return transformFilteredQuotation(filteredQuotation, employees);
  }, [filteredQuotation, employees, transformFilteredQuotation]);

  const groupedData = useMemo(() => {
    return groupByTypeService(processedData);
  }, [processedData]);


  // console.log("groupedData", groupedData)

  const finalData = useMemo(() => {
    if (!groupedData) return [];

    return Object.entries(groupedData).map(([typeService, items]) => {
      const first = items[0];
      const projectName = first?.projectName || "";
      const quotationId = first?.quotationId ?? projectName;
      const CostPerService = items.reduce(
        (sum, item) => sum + (item.grandTotal || 0),
        0
      );

      return {
        ProjectName: projectName,
        quotationId,
        CostPerService,
        TypeService: typeService,
      };
    });
  }, [groupedData]);



  const UpdateCostPerService = useCallback(async (selectedProject, finalData) => {


    try {
      // Dispatch updateCostPerService for each entry in finalData
      const updatePromises = finalData.map(item =>
        dispatch(updateCostPerService({
          quotationId: item.quotationId || item.ProjectName,
          TypeService: item.TypeService,
          CostPerService: item.CostPerService
        }))
      );

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      // Fetch leads after all updates are done
      dispatch(fetchLeads());

    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar("Failed to update some services", {
        variant: "error",
      });
    } finally {

    }
  }, [dispatch, enqueueSnackbar]);

  useEffect(() => {

    if (finalData.length > 0) {
      // console.log("finalData", finalData)
      UpdateCostPerService(selectedProjectName, finalData)

    }


  }, [UpdateCostPerService, selectedProjectName, finalData]);

  return (
    <div className="rounded-md">
      <div className="relative  scroll-visible justify-center top-0 pt-2">
        <div className="w-full relative pt-4 bg-white px-2 flex  align-middle items-center rounded-md">
          <div>
            <h1 className="text-xl font-semibold">Profit and Loss (P&L)</h1>
          </div>
        </div>

        <div className="flex justify-between gap-2 bg-white rounded-md p-1">
          <div className="flex">
            <div className="flex gap-3 p-1">
              <div
                className="relative flex gap-2 justify-center align-middle items-center"
                ref={dropdownRefGroup}
              >
                <label
                  htmlFor="selectedStatus"
                  className="block text-sm font-semibold text-gray-700 "
                >
                  GROUP
                </label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsGroupOpen(!isGroupOpen);
                  }}
                  className="w-24 relative outline-none date-picker-placeholder flex justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                >
                  <span className="truncate min-w-0 flex-1 text-left">
                    {AssignedGroup === "All" ? "All" : getGroupNameById(AssignedGroup)}
                  </span>
                  <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                </button>
                {isGroupOpen && (
                  <div className="absolute left-0 top-full w-full text-xs border overflow-hidden text-wrap border-gray-400 bg-white shadow-md rounded-md z-50">
                    <div
                      className="p-2 hover:bg-gray-200 cursor-pointer"
                      onClick={() => {
                        setAssignedGroup("All");
                        setIsGroupOpen(false);
                        setShowAllLeadTable(true);
                      }}
                    >
                      All
                    </div>
                    {GroupNamesL3.map((group) => (
                      <div
                        key={group._id || group.id}
                        className={`flex items-center p-2 hover:bg-gray-200 cursor-pointer ${String(AssignedGroup) === String(group._id || group.id)
                          ? "bg-gray-300"
                          : ""
                          }`}
                        onClick={() => {
                          setAssignedGroup(group._id || group.id);
                          setIsGroupOpen(false);
                          setShowAllLeadTable(true);
                          setClientFilter("");
                        }}
                      >
                        <label>{group.newGroupName || group.name}</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 align-middle items-center scale-90 z-50">
              <h1 className="font-semibold">CLIENT</h1>
              <div className="relative" ref={dropdownRefclient}>
                <div className="flex  p-0 border rounded-md align-middle items-center">
                  <ExpandMoreIcon
                    className="absolute right-1 cursor-pointer bg-white"
                    onClick={() => setShowClientDropdown(!showClientDropdown)}
                  />

                  <button
                    type="button"
                    onClick={() => setShowClientDropdown(!showClientDropdown)}
                    className=" text-left text-sm outline-none p-2 text-nowrap truncate w-40"
                  >
                    {clientFilter ? clientFilter : <p>Select Client</p>}
                  </button>
                </div>

                {showClientDropdown && (
                  <div
                    className="absolute top-full left-0 w-full max-h-64 cursor-pointer overflow-y-auto bg-gray-100 text-sm border border-gray-300 shadow-lg z-50"
                  >
                    <p
                      className="p-2 hover:bg-gray-200 "
                      onClick={() => {
                        handleClientChange("All");
                        setShowAllLeadTable(true);
                      }}
                    >
                      All
                    </p>
                    {uniqueClients.map((clientName, idx) => (
                      <p
                        key={idx}
                        className="p-2 hover:bg-gray-200"
                        onClick={() => {
                          handleClientChange(clientName);
                          setShowAllLeadTable(true);
                        }}
                      >
                        {clientName}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 p-1">
              <div
                className="relative flex gap-2 justify-center items-center"
                ref={dropdownRefYear}
              >
                <label className="block text-sm font-semibold text-gray-700">
                  YEAR
                </label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsYearOpen(!isYearOpen);
                  }}
                  className="w-24 relative outline-none date-picker-placeholder flex gap-1 justify-start border border-[#e0e7ff] rounded-md p-2 text-xs"
                >
                  <span className="truncate min-w-0 flex-1 text-left">
                    {selectedYears.length === 0
                      ? "All"
                      : selectedYears.join(", ").length < 14 // Adjust length limit as needed
                        ? selectedYears.join(", ")
                        : "Multiple"}
                  </span>
                  <ExpandMoreIcon className="absolute cursor-pointer right-1 top-1 bg-white" />
                </button>

                {isYearOpen && (
                  <div className="absolute left-0 top-full w-56 text-xs border overflow-hidden border-gray-400 bg-white shadow-md rounded-md z-50 max-h-64 overflow-y-auto">
                    {yearData.map((item) => {
                      const isYearSelected = selectedYears.includes(
                        item.Year.toString()
                      );

                      return (
                        <div
                          key={item.Year}
                          className="border-b border-gray-200"
                        >
                          <div className="flex align-middle items-center px-2">
                            <input
                              type="checkbox"
                              checked={selectedYears.includes(item.Year) || isYearSelected}
                              onChange={(e) => {
                                handleYearSelection(item.Year);
                              }}
                            />{" "}
                            <button
                              className={`w-full text-left px-3 py-2 font-medium cursor-pointer`}
                              onClick={() => {
                                setExpandedYear(
                                  expandedYear === item.Year ? null : item.Year
                                );
                              }}
                            >
                              {item.Year}
                            </button>
                          </div>

                          {expandedYear === item.Year && (
                            <div className="px-4 pb-3 text-gray-600">
                              <div className="text-[10px] font-semibold">
                                Months:
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1 text-[11px]">
                                {item.months.map((month) => {
                                  const value = `${item.Year} ${month}`;
                                  const isSelected =
                                    selectedYears.includes(value);

                                  return (
                                    <span
                                      key={month}
                                      onClick={() => handleYearSelection(value)}
                                      className={`px-2 py-[2px] rounded cursor-pointer border text-[11px]
                                ${isSelected
                                          ? "bg-blue-600 text-white border-blue-600"
                                          : "bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100"
                                        }`}
                                    >
                                      {month}
                                    </span>
                                  );
                                })}
                              </div>

                              <div className="mt-2 text-[10px] font-semibold">
                                Quarters:
                              </div>
                              <div className="flex flex-wrap gap-1 mt-1 text-[11px]">
                                {item.quarter.map((qtr) => {
                                  const value = `${item.Year} ${qtr}`;
                                  const isSelected =
                                    selectedYears.includes(value);

                                  return (
                                    <span
                                      key={qtr}
                                      onClick={() => handleYearSelection(value)}
                                      className={`px-2 py-[2px] rounded cursor-pointer border text-[11px]
                                ${isSelected
                                          ? "bg-green-600 text-white border-green-600"
                                          : "bg-green-50 text-green-800 border-green-100 hover:bg-green-100"
                                        }`}
                                    >
                                      {qtr}
                                    </span>
                                  );
                                })}

                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-4 align-middle items-center pr-4">
            {/* {financeTab !== "graph" && ( */}
            <div className="flex gap-2 p-1 align-middle items-center bg-[#e0e7ff] rounded-full">
              <MdSearch className="w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Search"
                className="bg-[#e0e7ff] outline-none w-[80%]"
              />
            </div>
          </div>
        </div>

        <>
          <div ref={QuotationRef}
            className="">
            {showAllLeadTable && (
              <div>
                <div className="w-full max-h-[80vh] overflow-auto">
                  <div className="w-full head sticky top-0 z-10 h-12 flex text-black bg-[#E8E8E8] font-semibold">
                    <div className="flex-shrink-0 w-10 px-3 py-2 flex items-center justify-start text-center border text-xs   tracking-wider bg-[#E8E8E8]">
                      Sr. No
                    </div>
                    <div className="w-[16.6%] px-3 py-2 flex gap-2  justify-between align-middle items-center text-center border text-xs tracking-wider capitalize bg-[#E8E8E8]">
                      Group Name
                      <div className="flex flex-col gap-y-1">
                        <div
                          className="cursor-pointer"
                          onClick={() =>
                            sortAlphabetically("groupname", "az")
                          }
                        >
                          <ArrowDropUp
                            color="white"
                            sx={{ fontSize: "22px" }}
                          />
                        </div>

                        <div
                          className="cursor-pointer -mt-2"
                          onClick={() =>
                            sortAlphabetically("groupname", "za")
                          }
                        >
                          <ArrowDropDown
                            color="white"
                            sx={{ fontSize: "22px" }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className=" w-[16.6%] px-3 py-2 flex gap-2  justify-between align-middle items-center text-center border text-xs tracking-wider capitalize bg-[#E8E8E8]">
                      Client Name
                      <div className="flex flex-col gap-y-1">
                        <div
                          className="cursor-pointer"
                          onClick={() =>
                            sortAlphabetically("clientname", "az")
                          }
                        >
                          <ArrowDropUp
                            color="white"
                            sx={{ fontSize: "22px" }}
                          />
                        </div>

                        <div
                          className="cursor-pointer -mt-2"
                          onClick={() =>
                            sortAlphabetically("clientname", "za")
                          }
                        >
                          <ArrowDropDown
                            color="white"
                            sx={{ fontSize: "22px" }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className=" w-[16.6%] px-3 py-2 flex gap-2  justify-between align-middle items-center text-center border text-xs tracking-wider capitalize bg-[#E8E8E8]">
                      Project Name
                      <div className="flex flex-col gap-y-1">
                        <div
                          className="cursor-pointer"
                          onClick={() =>
                            sortAlphabetically("projectname", "az")
                          }
                        >
                          <ArrowDropUp
                            color="white"
                            sx={{ fontSize: "22px" }}
                          />
                        </div>

                        <div
                          className="cursor-pointer -mt-2"
                          onClick={() =>
                            sortAlphabetically("projectname", "za")
                          }
                        >
                          <ArrowDropDown
                            color="white"
                            sx={{ fontSize: "22px" }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className=" w-[16.6%] px-3 py-2 flex justify-center align-middle items-center text-center border text-xs tracking-wider capitalize bg-[#E8E8E8]">
                      Quoted Expenses ({currency})
                    </div>
                    <div className=" w-[16.6%] px-3 py-2 flex justify-center align-middle items-center text-center border text-xs tracking-wider capitalize bg-[#E8E8E8]">
                      Actual Expenses ({currency})
                    </div>
                    <div className=" w-[16.6%] px-3 py-2 flex justify-center align-middle items-center text-center border text-xs tracking-wider capitalize bg-[#E8E8E8]">
                     Difference ({currency})
                    </div>
                  </div>
                  <div className="w-full">
                    {currentItems?.filter(row => {
                      const rowId = getRowQuotationId(row);
                      const quotedBase = Number(showExpensesop(rowId)) || 0;
                      const actualBase = Number(showExpensesopActual(rowId)) || 0;
                      const quoted = quotedBase;
                      const actual = actualBase;
                      return (quoted + actual) !== 0;
                    }).length === 0 ? (
                      <div className="bg-white w-full flex justify-center items-center py-5">
                        <div className="text-red-500 text-lg font-semibold">
                          No data available
                        </div>
                      </div>
                    ) : (
                      currentItems?.filter(row => {
                        const rowId = getRowQuotationId(row);
                        const quotedBase = Number(showExpensesop(rowId)) || 0;
                        const actualBase = Number(showExpensesopActual(rowId)) || 0;
                        const quoted = quotedBase;
                        const actual = actualBase;
                        return (quoted + actual) !== 0;
                      }).map((row, index) => {
                        const rowId = getRowQuotationId(row);
                        const getColor = showColor(rowId);
                        const quotedBase = Number(showExpensesop(rowId)) || 0;
                        const actualBase = Number(showExpensesopActual(rowId)) || 0;
                        const quoted = quotedBase;
                        const actual = actualBase;
                        const showQuoted = shouldShowQuotedExpenseForRow(rowId);
                        const isFormerManager = wasReassignedFromLoggedManager(rowId);
                        const showActualDash =
                          (showQuoted && isFormerManager && actual === 0) ||
                          (!showQuoted && actual === 0);
                        const diff = !showQuoted
                          ? actual
                          : isFormerManager && actual === 0
                            ? quoted
                            : quoted - actual;
                        const displayQuoted = showQuoted ? formatDecimalAmount(quoted) : "-";

                        return (
                          <div key={row.quotationId ?? rowId ?? index} className={`CRMandERPtableone w-full flex h-10`}>
                            <div
                              onClick={() => { if (isViewOnly) return; if (quoted !== 0) setSelectedProjectName(row); }}
                              className={`flex-shrink-0 w-10 bg-white ${isViewOnly ? "cursor-not-allowed pointer-events-none" : ""} px-3 py-3 flex items-center justify-start text-center border-b text-xs tracking-wider `}>
                              {startIdx + index + 1}
                            </div>
                            <div
                              onClick={() => { if (isViewOnly) return; if (quoted !== 0) setSelectedProjectName(row); }}
                              title="Click To View Finance Details"
                              className={` w-[16.6%] bg-white ${isViewOnly ? "cursor-not-allowed pointer-events-none" : ""} px-3 py-3 flex gap-2  justify-start align-middle items-center text-center border-b text-xs tracking-wider capitalize `}
                            >
                              {getGroupDisplayName(row)}
                            </div>
                            <div
                              onClick={() => { if (isViewOnly) return; if (quoted !== 0) setSelectedProjectName(row); }}
                              title="Click To View Finance Details"
                              className={` w-[16.6%]  bg-white ${isViewOnly ? "cursor-not-allowed pointer-events-none" : ""} px-3 py-3 flex gap-2  justify-start align-middle items-center text-center border-b text-xs tracking-wider capitalize `}
                            >
                              {row.ClientName}
                            </div>
                            <div
                              onClick={() => { if (isViewOnly) return; if (quoted !== 0) setSelectedProjectName(row); }}
                              title="Click To View Finance Details"
                              className={` w-[16.6%] bg-white ${isViewOnly ? "cursor-not-allowed pointer-events-none" : ""} px-3 py-3 flex gap-2  justify-start align-middle items-center text-start border-b text-xs  tracking-wider capitalize `}
                            >
                              {row.ProjectName}
                            </div>

                            <div
                              onClick={() => { if (isViewOnly) return; if (quoted !== 0) setSelectedProjectName(row); }}
                              className={`quotedExpense bg-white relative group w-[16.6%] ${isViewOnly ? "cursor-not-allowed pointer-events-none" : ""} px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs tracking-wider capitalize`}
                            >
                              {displayQuoted}
                            </div>
                            <div
                              onClick={() => { if (isViewOnly) return; if (quoted !== 0 || actual !== 0) setSelectedProjectName(row); }}
                              className={`quotedExpense bg-white relative group w-[16.6%] ${showActualDash ? "" : getColor} ${isViewOnly ? "cursor-not-allowed pointer-events-none" : ""} px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs tracking-wider capitalize`}
                            >
                              {showActualDash ? "-" : formatDecimalAmount(actual)}
                            </div>
                            <div
                              onClick={() => { if (isViewOnly) return; if (quoted !== 0 || actual !== 0) setSelectedProjectName(row); }}
                              className={`quotedExpense bg-white relative group w-[16.6%]  ${showQuoted && !isFormerManager && diff < 0 ? "text-red-700" : ""} ${isViewOnly ? "cursor-not-allowed pointer-events-none" : ""} px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs tracking-wider capitalize`}
                            >
                              {formatDecimalAmount(diff)}
                            </div>
                          </div>
                        )
                      }))}
                  </div>
                </div>
              </div>
            )}
            <div className="pagination border flex justify-between items-center bg-white mb-2 pt-2 pb-2 p-1 rounded-b-md">
              <div>
                {totalItems > 0 ? (
                  <>
                    Showing {startIdx + 1} to {Math.min(endIdx, totalItems)}{" "}
                    of {totalItems} Entries
                  </>
                ) : (
                  <>Showing 0 Entries</>
                )}
              </div>
              {totalPages > 1 && (
                <div>
                  <button
                    onClick={() => setCurrentPage(1)}
                    className="px-2 py-2 mx-1 border rounded"
                    disabled={currentPage === 1}
                    title="First Page"
                  >
                    <KeyboardDoubleArrowLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="px-2 py-2 mx-2 border rounded"
                    disabled={currentPage === 1}
                  >
                    <ChevronRightIcon className="w-5 h-5 rotate-180" />

                    {/* Previous */}
                  </button>

                  {Array.from({ length: 3 }, (_, index) => {
                    // Calculate the page number for the current range
                    const pageNumber = currentPage - 1 + index;

                    // Ensure the pageNumber stays within bounds
                    if (pageNumber >= 1 && pageNumber <= totalPages) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-4 py-2 mx-2 border rounded ${currentPage === pageNumber ? "bg-gray-300" : ""
                            }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    }

                    // Return null for out-of-bounds values
                    return null;
                  })}

                  <button
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(prev + 1, totalPages)
                      )
                    }
                    className="px-2 py-2 mx-2 border rounded"
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRightIcon className="w-5 h-5" />

                    {/* Next */}
                  </button>

                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className="px-2 py-2 mx-1 border rounded"
                    disabled={currentPage === totalPages}
                    title="Last Page"
                  >
                    <KeyboardDoubleArrowRightIcon className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </>

        <div></div>
      </div>
    </div>
  )
}

export default ManagerFinance
